import { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedIcon from "@mui/icons-material/Verified";
import RouteIcon from "@mui/icons-material/Route";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import ForestIcon from "@mui/icons-material/Forest";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { APP_ASSETS_SVG_SHED } from "libraries/svgPaths";
import { useAppSelector } from "redux/hooks";
import { selectReferenceElectricGridCountry } from "redux/slices/shed/shedSlice";
import {
  getElectricGridIntensity,
  getElectricGridLabel,
} from "libraries/emission";
import useIsMobile from "hooks/base-app/useIsMobile";

const SHED_URL = "https://shed.no/";
const RAINFOREST_URL = "https://www.regnskog.no/";

const PaperPanel = styled(Paper)(({ theme }) => ({
  bottom: theme.spacing(2.5),
  right: theme.spacing(1),
  borderRadius: 12,
  position: "fixed",
  padding: theme.spacing(1.25, 1.5),
  maxWidth: 300,
  width: "calc(100vw - 24px)",
  overflow: "visible",
  border: `1px solid ${theme.palette.divider}`,
}));

const MethodRow = ({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) => (
  <Stack direction="row" spacing={0.75} alignItems="flex-start">
    <Box color="success.main" mt={0.1} flexShrink={0} sx={{ "& svg": { fontSize: 15 } }}>
      {icon}
    </Box>
    <Typography variant="caption" color="text.secondary" lineHeight={1.35}>
      {text}
    </Typography>
  </Stack>
);

const MyLittleFranceCredibilityWidget = () => {
  const isMobile = useIsMobile();
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const isExpanded = userExpanded ?? !isMobile;
  const gridCountry = useAppSelector(selectReferenceElectricGridCountry);
  const gridLabel = getElectricGridLabel(gridCountry);
  const gridIntensity = getElectricGridIntensity(gridCountry);

  return (
  <PaperPanel elevation={3}>
    <Stack spacing={1}>
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          onClick={() => setUserExpanded(!isExpanded)}
          sx={{ cursor: "pointer" }}
        >
          <Typography
            variant="caption"
            color="text.disabled"
            textTransform="uppercase"
            letterSpacing={0.5}
            fontWeight={600}
            fontSize="0.75rem"
          >
            How this estimate works
          </Typography>
          <IconButton
            size="small"
            aria-label={isExpanded ? "Collapse estimate details" : "Expand estimate details"}
            aria-expanded={isExpanded}
            sx={{ p: 0.25, color: "text.disabled" }}
          >
            {isExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Stack>
        <Collapse in={isExpanded}>
          <Stack spacing={1} mt={0.5}>
            <Stack spacing={0.75}>
              <MethodRow
                icon={<RouteIcon />}
                text="Route distance × verified vehicle emission data."
              />
              <MethodRow
                icon={<ElectricBoltIcon />}
                text={`EV models use the ${gridLabel} electricity grid factor (~${gridIntensity} g CO₂/kWh).`}
              />
              <MethodRow
                icon={<ForestIcon />}
                text="CO₂ shown as equivalent rainforest trees via Rainforest Foundation Norway."
              />
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <VerifiedIcon sx={{ fontSize: 16, color: "success.main" }} />
              <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
                In partnership with{" "}
                <Link
                  href={RAINFOREST_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="success.main"
                  fontWeight={600}
                >
                  Rainforest Foundation Norway
                </Link>
              </Typography>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box
                component="img"
                src={APP_ASSETS_SVG_SHED}
                alt="Shed"
                sx={{ width: 44, height: 44, flexShrink: 0 }}
              />
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                  Powered by{" "}
                  <Link
                    href={SHED_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="secondary.main"
                    fontWeight={700}
                  >
                    Shed
                  </Link>
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
                  Making rainforest protection real.
                </Typography>
                <Button
                  component="a"
                  href={SHED_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  color="secondary"
                  size="small"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    px: 0,
                    minHeight: 28,
                    mt: 0.25,
                  }}
                >
                  Learn more at shed.no
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  </PaperPanel>
  );
};

export default MyLittleFranceCredibilityWidget;
